import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  LinearProgress,
  InputAdornment,
  IconButton,
  Divider,
  Fade,
  Slide,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Login as LoginIcon,
  Security,
  Business,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Link as MuiLink } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";

const Login: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(
    null
  );
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutTime, setLockoutTime] = useState<number | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Check for lockout status on component mount
  useEffect(() => {
    const savedLockout = localStorage.getItem("loginLockout");
    if (savedLockout) {
      const lockoutData = JSON.parse(savedLockout);
      const now = Date.now();
      if (now < lockoutData.until) {
        setIsLockedOut(true);
        setLockoutTime(lockoutData.until);
      } else {
        localStorage.removeItem("loginLockout");
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLockedOut) {
      setError("Hesabınız kilitli. Lütfen bekleyin.");
      return;
    }

    // Form validation
    if (!username.trim()) {
      setError("Kullanıcı adı gereklidir.");
      return;
    }

    if (!password.trim()) {
      setError("Şifre gereklidir.");
      return;
    }

    setLoading(true);
    setError("");
    setRemainingAttempts(null);

    try {
      console.log("Login attempt for:", username);
      const userData = await login({ username, password });
      console.log("Login successful, userData:", userData);
      console.log("User role:", userData?.role);

      // Clear any lockout data on successful login
      localStorage.removeItem("loginLockout");
      setIsLockedOut(false);
      setLockoutTime(null);

      // Role-based navigation using the returned user data
      let targetRoute = "/";
      if (userData) {
        switch (userData.role) {
          case "salesperson":
            targetRoute = "/salesperson-dashboard";
            break;
          case "manager":
            targetRoute = "/manager-dashboard";
            break;
          case "warehouse":
            targetRoute = "/warehouse-dashboard";
            break;
          case "admin":
            targetRoute = "/dashboard";
            break;
          default:
            targetRoute = "/dashboard";
        }
      }

      console.log("Navigating to:", targetRoute);
      navigate(targetRoute, { replace: true });
    } catch (error: any) {
      console.error("Login error details:", {
        message: error.message,
        response: error.response,
        data: error.response?.data,
        status: error.response?.status,
      });

      const errorData = error.response?.data;

      if (error.response?.status === 429) {
        // Account locked out
        const lockoutUntil =
          Date.now() + (errorData.remainingTime || 15 * 60 * 1000);
        localStorage.setItem(
          "loginLockout",
          JSON.stringify({ until: lockoutUntil })
        );
        setIsLockedOut(true);
        setLockoutTime(lockoutUntil);
        setError(
          errorData.error ||
            "Hesabınız kilitli. Lütfen 15 dakika sonra tekrar deneyin."
        );
      } else if (error.response?.status === 401) {
        // Invalid credentials
        setError(
          errorData.error ||
            "Kullanıcı adı veya şifre hatalı. Lütfen tekrar deneyin."
        );

        if (errorData.remainingAttempts !== undefined) {
          setRemainingAttempts(errorData.remainingAttempts);
        }

        if (errorData.lockedOut) {
          const lockoutUntil = Date.now() + 15 * 60 * 1000;
          localStorage.setItem(
            "loginLockout",
            JSON.stringify({ until: lockoutUntil })
          );
          setIsLockedOut(true);
          setLockoutTime(lockoutUntil);
        }
      } else if (error.response?.status === 400) {
        setError(errorData.error || "Kullanıcı adı ve şifre gereklidir.");
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError("Giriş yapılırken hata oluştu. Lütfen tekrar deneyin.");
      }

      // Clear password field on error
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  const getRemainingTime = () => {
    if (!lockoutTime) return 0;
    const remaining = Math.max(0, lockoutTime - Date.now());
    return Math.ceil(remaining / 1000 / 60); // Convert to minutes
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Slide direction="up" in={true} mountOnEnter unmountOnExit>
          <Paper
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                color: "white",
                p: 4,
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: -50,
                  right: -50,
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.1)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -30,
                  left: -30,
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.1)",
                }}
              />

              <Business sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 700, mb: 1 }}
              >
                OrderApp
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Sipariş Yönetim Sistemi
              </Typography>
            </Box>

            {/* Form Section */}
            <Box sx={{ p: 4 }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{ mb: 3, textAlign: "center", fontWeight: 600 }}
              >
                Giriş Yap
              </Typography>

              {/* Error Messages */}
              <Fade in={!!error}>
                <Alert
                  severity={isLockedOut ? "warning" : "error"}
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    "& .MuiAlert-message": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    },
                  }}
                  onClose={() => setError("")}
                >
                  {error}
                  {isLockedOut && lockoutTime && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Kalan süre: {getRemainingTime()} dakika
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.max(0, (getRemainingTime() / 15) * 100)}
                        sx={{ mt: 1, height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  )}
                </Alert>
              </Fade>

              <Fade in={remainingAttempts !== null && remainingAttempts <= 3}>
                <Alert
                  severity="warning"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    "& .MuiAlert-message": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    },
                  }}
                >
                  Kalan deneme hakkı: {remainingAttempts}
                </Alert>
              </Fade>

              {/* Login Form */}
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  id="username"
                  label="Kullanıcı Adı"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLockedOut || loading}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />

                <TextField
                  fullWidth
                  name="password"
                  label="Şifre"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLockedOut || loading}
                  sx={{ mb: 4 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          disabled={isLockedOut || loading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || isLockedOut}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    background:
                      "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                    },
                    "&:disabled": {
                      background: "rgba(0, 0, 0, 0.12)",
                    },
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CircularProgress
                        size={24}
                        sx={{ mr: 1, color: "white" }}
                      />
                      Giriş Yapılıyor...
                    </Box>
                  ) : isLockedOut ? (
                    "Hesap Kilitli"
                  ) : (
                    <>
                      <LoginIcon sx={{ mr: 1 }} />
                      Giriş Yap
                    </>
                  )}
                </Button>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    veya
                  </Typography>
                </Divider>

                <Box sx={{ textAlign: "center" }}>
                  <MuiLink
                    href="#"
                    variant="body2"
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Şifrenizi mi unuttunuz?
                  </MuiLink>
                </Box>
              </Box>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                background: "rgba(0, 0, 0, 0.02)",
                borderTop: "1px solid rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <Security
                  sx={{ fontSize: 16, mr: 0.5, color: "text.secondary" }}
                />
                <Typography variant="caption" color="text.secondary">
                  Güvenli Bağlantı
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                © 2025 OrderApp. Tüm hakları saklıdır.
              </Typography>
            </Box>
          </Paper>
        </Slide>
      </Container>
    </Box>
  );
};

export default Login;
